/** Product tour DTOs for onboarding flows; shared by API (Mongo) and clients. Align with Typegoose subdocs in @pncl/models. */

export enum TourPopoverSide {
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'bottom',
  LEFT = 'left'
}

export enum TourPopoverAlign {
  START = 'start',
  CENTER = 'center',
  END = 'end'
}

export enum TourColorTheme {
  LIGHT = 'light',
  DARK = 'dark'
}

export enum TourRichContentType {
  IMAGE = 'image',
  VIDEO = 'video'
}

export enum TourStepDismissalType {
  EVENT = 'event',
  CLICK = 'click'
}

export enum TourSpaceRole {
  HOST = 'host',
  PARTICIPANT = 'participant',
  GUEST = 'guest'
}

export enum TourFrequency {
  ONCE = 'once',
  FORCE = 'force'
}

export const TOUR_SPACE_ROLE_VALUES = [
  TourSpaceRole.HOST,
  TourSpaceRole.PARTICIPANT,
  TourSpaceRole.GUEST
] as const;

export enum TourTriggerType {
  EVENT = 'event',
  HOVER = 'hover',
  ROUTE_CHANGE = 'route_change'
}

export interface TourEventTrigger {
  type: TourTriggerType.EVENT;
  eventName: string;
}

export interface TourHoverTrigger {
  type: TourTriggerType.HOVER;
  selector: string;
  eventName?: string;
}

export interface TourRouteChangeTrigger {
  type: TourTriggerType.ROUTE_CHANGE;
}

export type TourTrigger =
  | TourEventTrigger
  | TourHoverTrigger
  | TourRouteChangeTrigger;

export interface TourConditions {
  pagePath?: string;
  releaseVersion?: TourReleaseVersion;
}

export interface TourReleaseVersion {
  min?: number;
  max?: number;
}

export interface TourAudienceCriteria {
  institutionIds?: string[];
  personas?: string[];
  spaceRoles?: TourSpaceRole[];
}

export interface TourAudienceExclusions extends TourAudienceCriteria {
  apiManagedUsers?: boolean;
  iframeUsers?: boolean;
}

export interface TourAudience {
  include?: TourAudienceCriteria;
  exclude?: TourAudienceExclusions;
}

export interface TourRichContent {
  type: TourRichContentType;
  url: string;
  alt?: string;
}

export interface TourStepPopoverContent {
  title?: string;
  description: string;
  hero?: TourRichContent;
}

export interface TourStepPopoverStyle {
  customClass?: string;
  side?: TourPopoverSide;
  align?: TourPopoverAlign;
  showBackdrop?: boolean;
  offsetX?: number;
  offsetY?: number;
}

export interface TourStepDismissalConfig {
  dismissType: TourStepDismissalType;
  eventName?: string;
  selector?: string;
}

/** Tour-level dismissal and popover behavior; distinct from per-step `dismissConfig` rules. */
export interface TourDismissalConfig {
  showDismissButton?: boolean;
}

export interface TourStep {
  element: string;
  popoverContent: TourStepPopoverContent;
  popoverStyle?: TourStepPopoverStyle;
  dismissConfig?: TourStepDismissalConfig[];
}

export interface Tour {
  _id: string;
  active?: boolean;
  trigger: TourTrigger;
  theme?: TourColorTheme;
  conditions?: TourConditions;
  audience?: TourAudience;
  steps: TourStep[];
  dismissal?: TourDismissalConfig;
  frequency?: TourFrequency;
  priority?: number;
}

export interface ToursResponse {
  tours: Tour[];
}
